import Auth0, { Auth0DecodedHash } from 'auth0-js';

import { appConfig } from 'src/app';
import { setDetails, setLoggedIn, setTokenExpired } from 'src/store/slices/user-slice';
import store from 'src/store/store';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import {
    getStorage,
    setStorage,
    resetAuth0Storage,
    setTokenStorageExpired,
} from 'src/utils/local-storage-versioning';

import { auth0 } from './constants';
import { ErrorCodes } from './enum/ErrorCodes';
import { Auth0LoginResponse, Auth0SessionResult, UserDetailsPartial } from './types';

export const logoutUser = async () => {
    resetAuth0Storage();
    auth0.logout({
        returnTo: `${window.location.origin}/logout`,
        clientID: appConfig.auth0.authClientId,
    });
};

export const checkSession = async (): Promise<Auth0SessionResult | false> =>
    new Promise((resolve) => {
        auth0.checkSession(
            {
                responseType: 'id_token token',
                scope: 'openid profile read:current_user create:current_user_metadata update:current_user_metadata',
            },
            (err, result) => {
                if (err) return resolve(false);
                return resolve(result as Auth0SessionResult);
            }
        );
    });

export const isUserAuthenticated = async (): Promise<Auth0DecodedHash | null> => {
    const storage = getStorage();
    const idToken = storage.auth0?.id_token;

    const sessionResult = await checkSession();
    if (idToken && !sessionResult) {
        store.dispatch(setTokenExpired(true));
        setTokenStorageExpired(true);
        return null;
    }

    if (sessionResult) {
        store.dispatch(setLoggedIn(true));
        setStorage({
            auth0: {
                access_token: sessionResult.accessToken,
                id_token: sessionResult.idToken,
                tokenExpired: false,
            },
        });

        return sessionResult;
    }
    store.dispatch(setLoggedIn(false));
    return null;
};

export const loginUser = async (
    email: string,
    password: string,
    redirectUri?: string,
    t?: (key: string) => string
) => {
    try {
        return await new Promise<Auth0LoginResponse>((resolve, reject) => {
            auth0.login(
                {
                    realm: 'Username-Password-Authentication',
                    responseType: 'id_token token',
                    email,
                    password,
                    scope: 'openid profile read:current_user create:current_user_metadata update:current_user_metadata',
                    ...(!!redirectUri && { redirectUri }),
                },
                (err, result) => {
                    if (err) return reject(err);
                    return resolve(result as Auth0LoginResponse);
                }
            );
        });
    } catch (error) {
        let errorMessage = (error as Error).message;

        if (t) {
            if (error && error.code === ErrorCodes.ACCESS_DENIED) {
                errorMessage = t('login_dialog_incorrect_details');
            } else if (error && error.code === ErrorCodes.TOO_MANY_ATTEMPTS) {
                errorMessage = t('login_dialog_too_many_attempts');
            } else {
                errorMessage = t('login_dialog_unexpected_error');
            }
        }

        throw new Error(errorMessage);
    }
};

export const createUserAndLogIn = async (
    email: string,
    password: string,
    t?: (key: string) => string
) => {
    try {
        await new Promise<void>((resolve, reject) => {
            auth0.redirect.signupAndLogin(
                {
                    connection: 'Username-Password-Authentication',
                    email,
                    password,
                    scope: 'openid profile read:current_user create:current_user_metadata update:current_user_metadata',
                },
                (err) => {
                    if (err) return reject(err);
                    return resolve();
                }
            );
        });
    } catch (error) {
        let errorMessage = (error as Error).message;

        if (t) {
            if (error && error.code === ErrorCodes.USER_ALREADY_EXISTS) {
                errorMessage = t('your_details_user_exists_error');
            } else if (error.response && error.response.status === 403) {
                errorMessage = t('login_dialog_incorrect_details_server_error');
            } else {
                errorMessage = t('login_dialog_unexpected_error');
            }
        }

        throw new Error(errorMessage);
    }
};

export const updateUserMetadata = async (
    userId: string,
    metadata: UserDetailsPartial
): Promise<void> => {
    try {
        // Check if we have a valid session
        const sessionResult = await checkSession();

        if (!sessionResult) {
            throw new Error(ErrorMessage.AUTH0_ID_ERROR);
        }

        const { accessToken } = sessionResult;

        // Instantiate Auth0 Management client with access token
        const management = new Auth0.Management({
            domain: appConfig.auth0.authDomain as string,
            token: accessToken,
        });

        // Call the patchUserMetadata method to update the user's metadata
        await new Promise<void>((resolve, reject) => {
            management.patchUserMetadata(userId, metadata, (err, updatedUser) => {
                if (err) {
                    return reject(err);
                }
                store.dispatch(setDetails(updatedUser.user_metadata));
                return resolve();
            });
        });
    } catch (error) {
        throw new Error(ErrorMessage.AUTH0_METADATA_UPDATE_ERROR);
    }
};

export const changePasswordRequest = async (email: string): Promise<string> => {
    const response: string = await new Promise((resolve, reject) => {
        auth0.changePassword(
            {
                email,
                connection: 'Username-Password-Authentication',
            },
            (err, result) => {
                if (err) {
                    return reject(err);
                }
                return resolve(result);
            }
        );
    });

    return response;
};
